import React, { useState, useEffect } from 'react';
import { 
  getArbicsxProfits, 
  getArbicsxUserWithdrawals, 
  addArbicsxUserWithdrawal, 
  updateArbicsxUserWithdrawal, 
  deleteArbicsxUserWithdrawal,
  getArbicsxPersonalExpenses,
  addArbicsxPersonalExpense,
  updateArbicsxPersonalExpense,
  deleteArbicsxPersonalExpense,
  getArbicsxPaymentMethods,
  addArbicsxPaymentMethod,
  updateArbicsxPaymentMethod,
  toggleArbicsxPaymentMethodFavorite,
  deleteArbicsxPaymentMethod
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useModule } from '../../contexts/ModuleContext';
import { formatCurrency, formatDate, formatDateTime, getTodayString, groupByMonth, getMonthName } from '../../lib/utils';
import { cryptoNetworks, getCryptoLogoByType } from '../../lib/cryptoLogos';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Trash2, Pencil, TrendingUp, Wallet, Lock, Star, Copy, CreditCard, ChevronDown, ChevronRight, Check, Settings2, ArrowDownCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PaginationControls } from '../../components/ui/pagination-controls';

export default function ArbicsxPagamentiPerNoi() {
  const { verifyPassword } = useAuth();
  const { impersonatedUser, isImpersonating } = useModule();
  const [profits, setProfits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [savedMethods, setSavedMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showMethodsModal, setShowMethodsModal] = useState(false);
  const [showAddMethodModal, setShowAddMethodModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: null, id: null });
  const [expensesUnlocked, setExpensesUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  
  const [editingWithdrawal, setEditingWithdrawal] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingMethod, setEditingMethod] = useState(null);
  
  const [withdrawalForm, setWithdrawalForm] = useState({
    date: getTodayString(),
    time: new Date().toTimeString().slice(0, 5),
    amount: '',
    method: '',
    account_holder: '',
    account_holder_surname: '',
    iban: '',
    bic_swift: '',
    wallet_name: '',
    wallet_address: '',
    network: '',
    crypto_type: '',
    hash: '',
    notes: ''
  });
  
  const [expenseForm, setExpenseForm] = useState({
    date: getTodayString(),
    amount: '',
    description: '',
    currency: 'USD'
  });

  const [methodForm, setMethodForm] = useState({
    name: '',
    type: 'crypto',
    wallet_address: '',
    wallet_name: '',
    network: '',
    crypto_type: '',
    iban: '',
    account_holder: '',
    account_holder_surname: '',
    bic_swift: ''
  });

  const impersonateId = isImpersonating ? impersonatedUser?.id : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profitsRes, withdrawalsRes, expensesRes, methodsRes] = await Promise.all([
        getArbicsxProfits(impersonateId, 1, 200),
        getArbicsxUserWithdrawals(impersonateId, 1, 200),
        getArbicsxPersonalExpenses(impersonateId, 1, 200),
        getArbicsxPaymentMethods(impersonateId)
      ]);
      setProfits(profitsRes.data.items || []);
      setWithdrawals(withdrawalsRes.data.items || []);
      setExpenses(expensesRes.data.items || []);
      setSavedMethods(methodsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [impersonateId]);

  // Calculations - in Arbicsx all profit goes to user (gross_amount = user_amount)
  // For "Prelevabile", only count profits up to today
  const today = getTodayString();
  
  // Total earned (all profits)
  const totalGuadagnato = profits.reduce((sum, p) => sum + (p.gross_amount || 0), 0);
  
  // Total withdrawn (all withdrawals - for history display)
  const totalPrelevato = withdrawals.reduce((sum, w) => sum + (w.amount || 0), 0);
  
  // For Prelevabile calculation: only count withdrawals with date <= today
  // Withdrawals with past dates (already happened) count towards what's been withdrawn
  // but we calculate Prelevabile based on current state
  const totalPrelevatoPerCalcolo = withdrawals
    .filter(w => w.date <= today)
    .reduce((sum, w) => sum + (w.amount || 0), 0);
  
  const totalSpese = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  
  // Prelevabile = Total earned - withdrawals up to today - expenses
  const prelevabile = totalGuadagnato - totalPrelevatoPerCalcolo - totalSpese;

  const withdrawalsByMonth = groupByMonth(withdrawals);

  const handleCopy = async (text, field) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleVerifyPassword = async () => {
    if (!passwordInput) {
      toast.error('Inserisci la password');
      return;
    }
    try {
      const valid = await verifyPassword(passwordInput);
      if (valid) {
        setExpensesUnlocked(true);
        setPasswordInput('');
      }
    } catch (error) {
      toast.error('Password non valida');
    }
  };

  const handleSelectSavedMethod = (method) => {
    if (method.type === 'bank') {
      setWithdrawalForm(prev => ({
        ...prev,
        method: 'Bonifico Bancario',
        account_holder: method.account_holder || '',
        account_holder_surname: method.account_holder_surname || '',
        iban: method.iban || '',
        bic_swift: method.bic_swift || ''
      }));
    } else {
      setWithdrawalForm(prev => ({
        ...prev,
        method: 'Crypto',
        wallet_name: method.wallet_name || method.name || '',
        wallet_address: method.wallet_address || '',
        network: method.network || '',
        crypto_type: method.crypto_type || ''
      }));
    }
    toast.success(`Metodo "${method.name}" selezionato`);
  };

  const handleSubmitWithdrawal = async () => {
    if (!withdrawalForm.amount || !withdrawalForm.method) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    try {
      const data = { ...withdrawalForm, amount: parseFloat(withdrawalForm.amount) };
      
      if (editingWithdrawal) {
        await updateArbicsxUserWithdrawal(editingWithdrawal.id, data, impersonateId);
        toast.success('Prelievo aggiornato');
      } else {
        await addArbicsxUserWithdrawal(data, impersonateId);
        toast.success('Prelievo registrato');
      }
      
      setShowWithdrawalModal(false);
      resetWithdrawalForm();
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleSubmitExpense = async () => {
    if (!expenseForm.amount || !expenseForm.description) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    try {
      const data = { ...expenseForm, amount: parseFloat(expenseForm.amount) };
      
      if (editingExpense) {
        await updateArbicsxPersonalExpense(editingExpense.id, data, impersonateId);
        toast.success('Spesa aggiornata');
      } else {
        await addArbicsxPersonalExpense(data, impersonateId);
        toast.success('Spesa registrata');
      }
      
      setShowExpenseModal(false);
      setEditingExpense(null);
      setExpenseForm({ date: getTodayString(), amount: '', description: '', currency: 'USD' });
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleSubmitMethod = async () => {
    if (!methodForm.name || !methodForm.type) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    try {
      if (editingMethod) {
        await updateArbicsxPaymentMethod(editingMethod.id, methodForm, impersonateId);
        toast.success('Metodo aggiornato');
      } else {
        await addArbicsxPaymentMethod(methodForm, impersonateId);
        toast.success('Metodo salvato');
      }
      
      setShowAddMethodModal(false);
      setEditingMethod(null);
      setMethodForm({
        name: '', type: 'crypto', wallet_address: '', wallet_name: '', network: '', 
        crypto_type: '', iban: '', account_holder: '', account_holder_surname: '', bic_swift: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteTarget.type === 'withdrawal') {
        await deleteArbicsxUserWithdrawal(deleteTarget.id, impersonateId);
      } else if (deleteTarget.type === 'expense') {
        await deleteArbicsxPersonalExpense(deleteTarget.id, impersonateId);
      } else if (deleteTarget.type === 'method') {
        await deleteArbicsxPaymentMethod(deleteTarget.id, impersonateId);
      }
      toast.success('Eliminato');
      setShowDeleteDialog(false);
      setDeleteTarget({ type: null, id: null });
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleToggleFavorite = async (method) => {
    try {
      await toggleArbicsxPaymentMethodFavorite(method.id, impersonateId);
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const resetWithdrawalForm = () => {
    setEditingWithdrawal(null);
    setWithdrawalForm({
      date: getTodayString(),
      time: new Date().toTimeString().slice(0, 5),
      amount: '', method: '', account_holder: '', account_holder_surname: '',
      iban: '', bic_swift: '', wallet_name: '', wallet_address: '',
      network: '', crypto_type: '', hash: '', notes: ''
    });
  };

  const openWithdrawalDetails = (w) => {
    setSelectedDetails(w);
    setShowDetailsModal(true);
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="ht-page-h1">I miei profitti</h1>
          <p className="ht-page-sub truncate">
            Arbicsx {isImpersonating && `- ${impersonatedUser?.name}`}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:w-auto">
          <button
            type="button"
            onClick={() => setShowMethodsModal(true)}
            className="ht-btn justify-center"
            data-testid="manage-methods-btn"
          >
            <Settings2 size={14} />
            Metodi Salvati
          </button>
          <button
            type="button"
            onClick={() => {
              resetWithdrawalForm();
              setShowWithdrawalModal(true);
            }}
            className="ht-btn ht-btn-primary justify-center"
            data-testid="add-withdrawal-btn"
          >
            <Plus size={14} />
            Nuovo Prelievo
          </button>
        </div>
      </div>

      {/* Sintesi: Prelevabile in grande */}
      <div className="ht-panel">
        <div className="ht-hero">
          <div className="ht-hero-main">
            <span className="ht-hero-label">Prelevabile</span>
            <p className={`ht-hero-num ${prelevabile < 0 ? 'ht-warn' : 'ht-pos'}`} data-testid="prelevabile-amount">
              {formatCurrency(prelevabile)}
            </p>
            {prelevabile < 0 && (
              <span className="ht-tag ht-tag-warn mt-3" data-testid="prelevabile-warning">
                <AlertTriangle size={11} />
                Attenzione
              </span>
            )}
          </div>
          <div className="ht-hero-side ht-rows">
            <div className="ht-row">
              <span className="ht-row-label"><TrendingUp size={12} /> Totale guadagnato</span>
              <span className="ht-row-value ht-pos" data-testid="total-guadagnato">{formatCurrency(totalGuadagnato)}</span>
            </div>
            <div className="ht-row">
              <span className="ht-row-label"><ArrowDownCircle size={12} /> Prelevati</span>
              <span className="ht-row-value" data-testid="total-prelevato">{formatCurrency(totalPrelevato)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="withdrawals" className="w-full">
        <TabsList className="ht-tabs bg-transparent border-0 p-0 h-auto">
          <TabsTrigger value="withdrawals" className="ht-tab">
            Prelievi ({withdrawals.length})
          </TabsTrigger>
          <TabsTrigger value="expenses" className="ht-tab">
            <Lock size={13} /> Spese ({expenses.length})
          </TabsTrigger>
        </TabsList>

        {/* Withdrawals */}
        <TabsContent value="withdrawals" className="mt-4">
          {Object.keys(withdrawalsByMonth).length === 0 ? (
            <div className="ht-panel ht-empty">Nessun prelievo registrato</div>
          ) : (
            <div className="space-y-2">
              {Object.keys(withdrawalsByMonth).sort().reverse().map(month => {
                const monthWithdrawals = withdrawalsByMonth[month];
                const monthTotal = monthWithdrawals.reduce((s, w) => s + (w.amount || 0), 0);
                const isExpanded = expandedMonths[month];
                
                return (
                  <div key={month} className="ht-acc" data-testid={`withdrawal-month-${month}`}>
                    <button
                      type="button"
                      onClick={() => toggleMonth(month)}
                      className="ht-acc-head"
                      data-testid={`withdrawal-month-toggle-${month}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {isExpanded
                          ? <ChevronDown size={14} className="ht-chev" />
                          : <ChevronRight size={14} className="ht-chev" />}
                        <span className="ht-acc-title truncate">{getMonthName(month)}</span>
                        <span className="ht-tag">{monthWithdrawals.length}</span>
                      </span>
                      <span className="ht-item-n">{formatCurrency(monthTotal)}</span>
                    </button>
                    
                    {isExpanded && (
                      <div className="ht-acc-body">
                        <div className="ht-list">
                          {monthWithdrawals.map(w => (
                            <div 
                              key={w.id}
                              className="ht-item cursor-pointer"
                              onClick={() => openWithdrawalDetails(w)}
                              data-testid={`withdrawal-row-${w.id}`}
                            >
                              <span className="flex-shrink-0 w-5 flex items-center justify-center">
                                {w.method === 'Crypto'
                                  ? (getCryptoLogoByType(w.crypto_type) || <CreditCard size={14} className="text-[#5f6672]" />)
                                  : <CreditCard size={14} className="text-[#5f6672]" />}
                              </span>
                              <div className="ht-item-main">
                                <p className="ht-item-t">{w.method}</p>
                                <p className="ht-item-s">{formatDateTime(w.date, w.time)}</p>
                              </div>
                              <span className="ht-item-n">{formatCurrency(w.amount)}</span>
                              <div className="ht-item-actions">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingWithdrawal(w);
                                    setWithdrawalForm({
                                      ...w,
                                      amount: w.amount?.toString() || ''
                                    });
                                    setShowWithdrawalModal(true);
                                  }}
                                  className="ht-btn ht-btn-icon"
                                  aria-label="Modifica prelievo"
                                >
                                  <Pencil size={12} />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget({ type: 'withdrawal', id: w.id });
                                    setShowDeleteDialog(true);
                                  }}
                                  className="ht-btn ht-btn-icon ht-btn-danger"
                                  aria-label="Elimina prelievo"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Expenses (Password Protected) */}
        <TabsContent value="expenses" className="mt-4">
          {!expensesUnlocked ? (
            <div className="ht-panel p-8 text-center">
              <Lock size={36} className="mx-auto mb-4 text-[#5f6672]" />
              <p className="text-sm text-[#8b919c] mb-4">Questa sezione è protetta da password</p>
              <div className="flex justify-center gap-2 max-w-xs mx-auto">
                <Input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  className="bg-[#11161d] border-[#282c34] text-white"
                  placeholder="Password"
                  onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
                />
                <button type="button" onClick={handleVerifyPassword} className="ht-btn ht-btn-primary flex-shrink-0">
                  Sblocca
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="text-xs text-[#8b919c]">
                  Totale spese <span className="ht-item-n ht-alert ml-1">{formatCurrency(totalSpese)}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setEditingExpense(null);
                    setExpenseForm({ date: getTodayString(), amount: '', description: '', currency: 'USD' });
                    setShowExpenseModal(true);
                  }}
                  className="ht-btn ht-btn-sm ht-btn-primary"
                  data-testid="add-expense-btn"
                >
                  <Plus size={13} />
                  Aggiungi Spesa
                </button>
              </div>
              
              {expenses.length === 0 ? (
                <div className="ht-panel ht-empty">Nessuna spesa registrata</div>
              ) : (
                <div className="ht-panel overflow-hidden">
                  <div className="ht-list">
                    {expenses.map(e => (
                      <div key={e.id} className="ht-item" data-testid={`expense-row-${e.id}`}>
                        <div className="ht-item-main">
                          <p className="ht-item-t">{e.description}</p>
                          <p className="ht-item-s">{formatDate(e.date)}</p>
                        </div>
                        <span className="ht-item-n ht-alert">-{formatCurrency(e.amount)}</span>
                        <div className="ht-item-actions">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingExpense(e);
                              setExpenseForm({ ...e, amount: e.amount?.toString() || '' });
                              setShowExpenseModal(true);
                            }}
                            className="ht-btn ht-btn-icon"
                            aria-label="Modifica spesa"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteTarget({ type: 'expense', id: e.id });
                              setShowDeleteDialog(true);
                            }}
                            className="ht-btn ht-btn-icon ht-btn-danger"
                            aria-label="Elimina spesa"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Withdrawal Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="ht-panel text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Dettagli Prelievo</DialogTitle>
          </DialogHeader>
          {selectedDetails && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-disabled">Data</p>
                  <p className="text-white">{formatDate(selectedDetails.date)}</p>
                </div>
                <div>
                  <p className="text-xs text-disabled">Ora</p>
                  <p className="text-white">{selectedDetails.time || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-disabled">Metodo</p>
                  <p className="text-white">{selectedDetails.method}</p>
                </div>
                <div>
                  <p className="text-xs text-disabled">Importo</p>
                  <p className="text-white font-mono">{formatCurrency(selectedDetails.amount)}</p>
                </div>
              </div>
              
              {selectedDetails.method === 'Bonifico Bancario' && (
                <div className="space-y-3 border-t border-gray-800 pt-4">
                  <div>
                    <p className="text-xs text-disabled">Nome</p>
                    <p className="text-white">{selectedDetails.account_holder || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-disabled">Cognome</p>
                    <p className="text-white">{selectedDetails.account_holder_surname || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-disabled">IBAN</p>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-mono text-sm">{selectedDetails.iban || '-'}</p>
                      {selectedDetails.iban && (
                        <Button
                          onClick={() => handleCopy(selectedDetails.iban, 'iban')}
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-gray-400 hover:text-white"
                        >
                          {copiedField === 'iban' ? <Check size={12} className="ht-pos" /> : <Copy size={12} />}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-disabled">BIC/SWIFT</p>
                    <p className="text-white font-mono text-sm">{selectedDetails.bic_swift || '-'}</p>
                  </div>
                </div>
              )}
              
              {selectedDetails.method === 'Crypto' && (
                <div className="space-y-3 border-t border-gray-800 pt-4">
                  <div>
                    <p className="text-xs text-disabled">Nome Wallet</p>
                    <p className="text-white">{selectedDetails.wallet_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-disabled">Wallet Address</p>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-mono text-xs break-all">{selectedDetails.wallet_address || '-'}</p>
                      {selectedDetails.wallet_address && (
                        <Button
                          onClick={() => handleCopy(selectedDetails.wallet_address, 'wallet')}
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 flex-shrink-0 text-gray-400 hover:text-white"
                        >
                          {copiedField === 'wallet' ? <Check size={12} className="ht-pos" /> : <Copy size={12} />}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-disabled">Rete</p>
                      <p className="text-white">{selectedDetails.network || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-disabled">Coin</p>
                      <p className="text-white">{selectedDetails.crypto_type || '-'}</p>
                    </div>
                  </div>
                  {selectedDetails.hash && (
                    <div>
                      <p className="text-xs text-disabled">Hash</p>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-mono text-xs break-all">{selectedDetails.hash}</p>
                        <Button
                          onClick={() => handleCopy(selectedDetails.hash, 'hash')}
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 flex-shrink-0 text-gray-400 hover:text-white"
                        >
                          {copiedField === 'hash' ? <Check size={12} className="ht-pos" /> : <Copy size={12} />}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowDetailsModal(false)} className="ht-btn ht-btn-primary">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdrawal Modal */}
      <Dialog open={showWithdrawalModal} onOpenChange={setShowWithdrawalModal}>
        <DialogContent className="ht-panel text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingWithdrawal ? 'Modifica Prelievo' : 'Nuovo Prelievo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Saved Methods Quick Select */}
            {savedMethods.length > 0 && !editingWithdrawal && (
              <div className="mb-4">
                <Label className="text-gray-300 text-xs">Metodi Salvati</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {savedMethods.filter(m => m.is_favorite).map(m => (
                    <Button
                      key={m.id}
                      onClick={() => handleSelectSavedMethod(m)}
                      size="sm"
                      variant="outline"
                      className="ht-btn ht-btn-sm"
                    >
                      <Star size={12} className="mr-1 fill-current" />
                      {m.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Data</Label>
                <Input
                  type="date"
                  value={withdrawalForm.date}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, date: e.target.value })}
                  className="bg-[#11161d] border-[#282c34] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Ora</Label>
                <Input
                  type="time"
                  value={withdrawalForm.time}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, time: e.target.value })}
                  className="bg-[#11161d] border-[#282c34] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Importo ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={withdrawalForm.amount}
                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Metodo</Label>
              <Select value={withdrawalForm.method} onValueChange={(v) => setWithdrawalForm({ ...withdrawalForm, method: v })}>
                <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                  <SelectValue placeholder="Seleziona metodo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bonifico Bancario" className="text-white">Bonifico Bancario</SelectItem>
                  <SelectItem value="Crypto" className="text-white">Crypto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bank Fields */}
            {withdrawalForm.method === 'Bonifico Bancario' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Nome</Label>
                    <Input
                      value={withdrawalForm.account_holder}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, account_holder: e.target.value })}
                      className="bg-[#11161d] border-[#282c34] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Cognome</Label>
                    <Input
                      value={withdrawalForm.account_holder_surname}
                      onChange={(e) => setWithdrawalForm({ ...withdrawalForm, account_holder_surname: e.target.value })}
                      className="bg-[#11161d] border-[#282c34] text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">IBAN</Label>
                  <Input
                    value={withdrawalForm.iban}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, iban: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">BIC/SWIFT</Label>
                  <Input
                    value={withdrawalForm.bic_swift}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, bic_swift: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
              </>
            )}

            {/* Crypto Fields */}
            {withdrawalForm.method === 'Crypto' && (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-300">Nome Wallet</Label>
                  <Input
                    value={withdrawalForm.wallet_name}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, wallet_name: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Wallet Address</Label>
                  <Input
                    value={withdrawalForm.wallet_address}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, wallet_address: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Rete</Label>
                    <Select value={withdrawalForm.network} onValueChange={(v) => setWithdrawalForm({ ...withdrawalForm, network: v })}>
                      <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {cryptoNetworks.map(n => (
                          <SelectItem key={n.value} value={n.value} className="text-white">{n.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Tipo Crypto</Label>
                    <Select value={withdrawalForm.crypto_type} onValueChange={(v) => setWithdrawalForm({ ...withdrawalForm, crypto_type: v })}>
                      <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USDT" className="text-white">USDT</SelectItem>
                        <SelectItem value="USDC" className="text-white">USDC</SelectItem>
                        <SelectItem value="BTC" className="text-white">BTC</SelectItem>
                        <SelectItem value="ETH" className="text-white">ETH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Hash (opzionale)</Label>
                  <Input
                    value={withdrawalForm.hash}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, hash: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label className="text-gray-300">Note (opzionale)</Label>
              <Input
                value={withdrawalForm.notes}
                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, notes: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowWithdrawalModal(false)} className="text-gray-400">
              Annulla
            </Button>
            <Button onClick={handleSubmitWithdrawal} className="ht-btn ht-btn-primary">
              {editingWithdrawal ? 'Aggiorna' : 'Registra'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Modal */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="ht-panel text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingExpense ? 'Modifica Spesa' : 'Nuova Spesa'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Data</Label>
              <Input
                type="date"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Importo</Label>
              <Input
                type="number"
                step="0.01"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Descrizione</Label>
              <Input
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
                placeholder="Descrizione spesa"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Valuta</Label>
              <Select value={expenseForm.currency} onValueChange={(v) => setExpenseForm({ ...expenseForm, currency: v })}>
                <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD" className="text-white">USD</SelectItem>
                  <SelectItem value="EUR" className="text-white">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowExpenseModal(false)} className="text-gray-400">
              Annulla
            </Button>
            <Button onClick={handleSubmitExpense} className="ht-btn ht-btn-primary">
              {editingExpense ? 'Aggiorna' : 'Registra'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saved Methods Modal */}
      <Dialog open={showMethodsModal} onOpenChange={setShowMethodsModal}>
        <DialogContent className="ht-panel text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Metodi di Pagamento Salvati</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button
              onClick={() => {
                setEditingMethod(null);
                setMethodForm({
                  name: '', type: 'crypto', wallet_address: '', wallet_name: '', network: '', 
                  crypto_type: '', iban: '', account_holder: '', account_holder_surname: '', bic_swift: ''
                });
                setShowAddMethodModal(true);
              }}
              className="ht-btn ht-btn-primary w-full"
            >
              <Plus size={16} />
              Aggiungi Metodo
            </Button>
            
            {savedMethods.length === 0 ? (
              <p className="text-center text-disabled py-4">Nessun metodo salvato</p>
            ) : (
              <div className="space-y-2">
                {savedMethods.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 rounded-[12px] border border-[#282c34] bg-[#11161d]">
                    <div className="flex items-center gap-3 min-w-0">
                      <CreditCard size={14} className="text-[#5f6672] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="ht-item-t">{m.name}</p>
                        <p className="ht-item-s">{m.type === 'bank' ? 'Bonifico' : 'Crypto'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        onClick={() => handleToggleFavorite(m)}
                        size="icon"
                        variant="ghost"
                        className={`h-7 w-7 ${m.is_favorite ? 'ht-pos' : 'text-[#5f6672]'}`}
                      >
                        <Star size={14} className={m.is_favorite ? 'fill-current' : ''} />
                      </Button>
                      <Button
                        onClick={() => {
                          setEditingMethod(m);
                          setMethodForm(m);
                          setShowAddMethodModal(true);
                        }}
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-gray-400 hover:text-white"
                      >
                        <Pencil size={12} />
                      </Button>
                      <Button
                        onClick={() => {
                          setDeleteTarget({ type: 'method', id: m.id });
                          setShowDeleteDialog(true);
                        }}
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowMethodsModal(false)} className="ht-btn">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Method Modal */}
      <Dialog open={showAddMethodModal} onOpenChange={setShowAddMethodModal}>
        <DialogContent className="ht-panel text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingMethod ? 'Modifica Metodo' : 'Nuovo Metodo di Pagamento'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Nome</Label>
              <Input
                value={methodForm.name}
                onChange={(e) => setMethodForm({ ...methodForm, name: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
                placeholder="es. Binance USDT, Banca XYZ"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Tipo</Label>
              <Select value={methodForm.type} onValueChange={(v) => setMethodForm({ ...methodForm, type: v })}>
                <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crypto" className="text-white">Crypto</SelectItem>
                  <SelectItem value="bank" className="text-white">Bonifico Bancario</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {methodForm.type === 'bank' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Nome</Label>
                    <Input
                      value={methodForm.account_holder}
                      onChange={(e) => setMethodForm({ ...methodForm, account_holder: e.target.value })}
                      className="bg-[#11161d] border-[#282c34] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Cognome</Label>
                    <Input
                      value={methodForm.account_holder_surname}
                      onChange={(e) => setMethodForm({ ...methodForm, account_holder_surname: e.target.value })}
                      className="bg-[#11161d] border-[#282c34] text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">IBAN</Label>
                  <Input
                    value={methodForm.iban}
                    onChange={(e) => setMethodForm({ ...methodForm, iban: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">BIC/SWIFT</Label>
                  <Input
                    value={methodForm.bic_swift}
                    onChange={(e) => setMethodForm({ ...methodForm, bic_swift: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
              </>
            )}

            {methodForm.type === 'crypto' && (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-300">Nome Wallet</Label>
                  <Input
                    value={methodForm.wallet_name}
                    onChange={(e) => setMethodForm({ ...methodForm, wallet_name: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Indirizzo Wallet</Label>
                  <Input
                    value={methodForm.wallet_address}
                    onChange={(e) => setMethodForm({ ...methodForm, wallet_address: e.target.value })}
                    className="bg-[#11161d] border-[#282c34] text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Tipo Rete</Label>
                    <Select value={methodForm.network} onValueChange={(v) => setMethodForm({ ...methodForm, network: v })}>
                      <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {cryptoNetworks.map(n => (
                          <SelectItem key={n.value} value={n.value} className="text-white">{n.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Tipo Crypto</Label>
                    <Select value={methodForm.crypto_type} onValueChange={(v) => setMethodForm({ ...methodForm, crypto_type: v })}>
                      <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USDT" className="text-white">USDT</SelectItem>
                        <SelectItem value="USDC" className="text-white">USDC</SelectItem>
                        <SelectItem value="BTC" className="text-white">BTC</SelectItem>
                        <SelectItem value="ETH" className="text-white">ETH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddMethodModal(false)} className="text-gray-400">
              Annulla
            </Button>
            <Button onClick={handleSubmitMethod} className="ht-btn ht-btn-primary">
              {editingMethod ? 'Aggiorna' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Questa azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="ht-btn border-0">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="ht-btn ht-btn-danger">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
